import { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  getDoc
} from "firebase/firestore";

import { updateDoc } from "firebase/firestore";
import { X } from "lucide-react";

export default function FavoritesModal({
  onClose,
  onJumpToMessage
}) {
  const [favorites, setFavorites] = useState([]);
  const [usersCache, setUsersCache] = useState({});
  const [chatsCache, setChatsCache] = useState({});
  const [loading, setLoading] = useState(true);

  const user = localStorage.getItem("user");

  // 🗓️ формат даты
  const getDateLabel = (ts) => {
    if (!ts) return "";

    const date = ts.seconds
      ? new Date(ts.seconds * 1000)
      : new Date(ts);

    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    if (isSameDay(date, today)) return "Сегодня";
    if (isSameDay(date, yesterday)) return "Вчера";

    return date.toLocaleDateString();
  };

  const removeFromFavorites = async (chatId, msgId, favoriteBy = []) => {
  try {
    const ref = doc(db, "chats", chatId, "messages", msgId);

    const updated = favoriteBy.filter(u => u !== user);

    await updateDoc(ref, {
      favoriteBy: updated
    });

    // 💥 сразу убираем из UI
    setFavorites(prev => prev.filter(m => m.id !== msgId));

  } catch (err) {
    console.error("Remove favorite error:", err);
  }
};

  // 📥 загрузка
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        setLoading(true);

        const chatsSnap = await getDocs(collection(db, "chats"));

        let allFavs = [];
        let usersToLoad = new Set();
        let chatsToLoad = new Set();

        await Promise.all(
          chatsSnap.docs.map(async (chatDoc) => {
            const chatId = chatDoc.id;

            try {
              const q = query(
                collection(db, "chats", chatId, "messages"),
                orderBy("createdAt", "desc")
              );

              const snap = await getDocs(q);

              const favs = snap.docs
                .map(doc => ({
                  id: doc.id,
                  chatId,
                  ...doc.data()
                }))
                .filter(m => (m.favoriteBy || []).includes(user));

              favs.forEach(m => {
                usersToLoad.add(m.sender);
                chatsToLoad.add(m.chatId);
              });

              allFavs.push(...favs);
            } catch {}
          })
        );

        // чаты
        const chats = {};
        await Promise.all(
          [...chatsToLoad].map(async (chatId) => {
            try {
              const snap = await getDoc(doc(db, "chats", chatId));
              if (snap.exists()) chats[chatId] = snap.data();
            } catch {}
          })
        );
        setChatsCache(chats);

        // сортировка
        allFavs.sort((a, b) => {
          const aTime = a.createdAt?.seconds || a.createdAt || 0;
          const bTime = b.createdAt?.seconds || b.createdAt || 0;
          return bTime - aTime;
        });

        setFavorites(allFavs);

        // пользователи
        const users = {};
        await Promise.all(
          [...usersToLoad].map(async (username) => {
            try {
              const snap = await getDoc(doc(db, "users", username));
              if (snap.exists()) users[username] = snap.data();
            } catch {}
          })
        );

        setUsersCache(users);

      } catch (err) {
        console.error("Favorites error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadFavorites();
  }, [user]);

  const getTime = (ts) => {
    if (!ts) return "";
    if (ts.seconds) return new Date(ts.seconds * 1000);
    return new Date(ts);
  };

  const getChatName = (chatId) => {
    const chat = chatsCache[chatId];

    if (!chat) return "Чат";

    if (chat.members && chat.members.length > 2) {
      return chat.name || "Группа";
    }

    const parts = chatId.split("_");
    const other = parts.find(u => u !== user);

    return other || "Чат";
  };

  // 🔥 группировка
  const grouped = Object.entries(
    favorites.reduce((acc, m) => {
      const key = getDateLabel(m.createdAt);
      if (!acc[key]) acc[key] = [];
      acc[key].push(m);
      return acc;
    }, {})
  );

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* HEADER */}
        <div style={styles.header}>
          <div style={styles.title}>
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="1.8"
  >
    <path d="M12 3l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.8 6.1 21l1.2-6.5L2.5 9.9 9 9z" />
  </svg>

  <span style={{ marginLeft: 8 }}>Избранное</span>
</div>
          <div style={styles.close} onClick={onClose}>✕</div>
        </div>

        {/* CONTENT */}
        <div style={styles.content}>

          {loading ? (
            <div style={styles.skeletonWrap}>
                {[...Array(5)].map((_, i) => (
                <div key={i} style={styles.skeletonItem}>
                    <div style={styles.skeletonHeader}>
                    <div style={styles.skeletonAvatar}></div>
                    <div style={{ flex: 1 }}>
                        <div style={styles.skeletonLineShort}></div>
                        <div style={styles.skeletonLineTiny}></div>
                    </div>
                    </div>

                    <div style={styles.skeletonLine}></div>
                    <div style={styles.skeletonLineSmall}></div>
                </div>
                ))}
            </div>
            ): favorites.length === 0 ? (
            <div style={styles.empty}>
              Нет избранных сообщений
            </div>
          ) : (
            grouped.map(([date, msgs]) => (
              <div key={date}>
                <div style={styles.dateDivider}>{date}</div>

                {msgs.map((m) => {
                  const userData = usersCache[m.sender];

                  return (
                    <div key={m.id} style={styles.item}>
                      <div style={styles.msgHeader}>
                        <img
                          src={userData?.avatar || "https://via.placeholder.com/40"}
                          style={styles.avatar}
                        />

                        <div style={{ flex: 1 }}>
                          <div style={styles.name}>
                            {userData?.name || m.sender}
                          </div>

                          <div style={styles.chat}>
                            {getChatName(m.chatId)}
                          </div>
                        </div>

                        <div style={styles.time}>
                          {getTime(m.createdAt)?.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </div>
                      </div>

                      {m.text && (
                        <div style={styles.text}>{m.text}</div>
                      )}

                      {m.image && (
                        <img src={m.image} style={styles.image} />
                      )}

                      <div style={styles.bottomRow}>
                        <div
                            style={styles.actionBtn}
                            onClick={() => removeFromFavorites(m.chatId, m.id, m.favoriteBy)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                                e.currentTarget.style.color = "#fff";
                                }}

                                onMouseLeave={(e) => {
                                e.currentTarget.style.background = "transparent";
                                e.currentTarget.style.color = "#aaa";
                                }}
                        >
                            <X size={14} />
                        </div>

                        <div
                            style={styles.actionBtn}
                                onClick={() => {
                                        onJumpToMessage(m.chatId, m.id);
  onClose();
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                                    e.currentTarget.style.color = "#fff";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                    e.currentTarget.style.color = "#aaa";
                                }}
                        >
                            ➤
                        </div>
                        </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}

        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(10px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
  },

  modal: {
  width: 360,
  maxHeight: "70vh",
  display: "flex",
  flexDirection: "column",
  background: "rgba(20,20,20,0.75)",
  backdropFilter: "blur(20px)",
  borderRadius: 16,
  border: "1px solid rgba(255,255,255,0.08)",
  overflow: "hidden",
  minHeight: 0 // 💥 ВОТ ЭТО ЛОМАЕТ СКЕЛЕТОН БЕЗ НЕГО
},

  header: {
    display: "flex",
    justifyContent: "space-between",
    padding: 12,
  },

  title: {
    fontWeight: 500
  },

  close: {
    cursor: "pointer"
  },

  content: {
  padding: 12,
  overflowY: "auto",
  flex: 1,
  minHeight: 0,
  display: "flex",          // 👈 добавь
  flexDirection: "column"   // 👈 добавь
},

  dateDivider: {
    textAlign: "center",
    fontSize: 12,
    opacity: 0.5,
    margin: "10px 0"
  },

  empty: {
    textAlign: "center",
    opacity: 0.5
  },

  item: {
    padding: 10,
    borderRadius: 12,
    background: "rgba(255,255,255,0.04)",
    marginBottom: 8
  },

  msgHeader: {
    display: "flex",
    gap: 8
  },

  avatar: {
    width: 28,
    height: 28,
    borderRadius: "50%"
  },

  name: {
    fontSize: 13
  },

  chat: {
    fontSize: 11,
    opacity: 0.5
  },

  text: {
    marginTop: 4
  },

  image: {
    width: "100%",
    borderRadius: 8,
    marginTop: 6
  },

  bottomRow: {
  display: "flex",
  justifyContent: "flex-end",
  gap: 6 // 👈 добавь это
},

  time: {
    fontSize: 11,
    opacity: 0.5
  },

  loader: {
    height: 120,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  spinner: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "3px solid rgba(255,255,255,0.15)",
    borderTop: "3px solid rgba(255,255,255,0.9)",
    animation: "spin 1s linear infinite"
  },

  skeletonWrap: {
  display: "flex",
  flexDirection: "column",
  gap: 10,
},

skeletonItem: {
  padding: 10,
  borderRadius: 12,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.05)",
},

skeletonHeader: {
  display: "flex",
  gap: 8,
  marginBottom: 8
},

skeletonAvatar: {
  width: 28,
  height: 28,
  borderRadius: "50%",
  background: "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.5s infinite"
},

skeletonLine: {
  height: 10,
  borderRadius: 6,
  marginBottom: 6,
  background: "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.5s infinite"
},

skeletonLineSmall: {
  height: 8,
  width: "60%",
  borderRadius: 6,
  background: "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.5s infinite"
},

skeletonLineShort: {
  height: 10,
  width: "40%",
  borderRadius: 6,
  marginBottom: 4,
  background: "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.5s infinite"
},

skeletonLineTiny: {
  height: 8,
  width: "25%",
  borderRadius: 6,
  background: "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.5s infinite"
},

  jumpBtn: {
    cursor: "pointer"
  },

  actionBtn: {
  width: 26,
  height: 26,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 8,
  cursor: "pointer",
  color: "#aaa",
  transition: "all 0.2s ease",
  backdropFilter: "blur(6px)",
  border: "1px solid rgba(255,255,255,0.06)" // 👈 subtle рамка
},
};